import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  OpenInNew as OpenInNewIcon,
  PlayArrow as RunIcon,
  Pause as PauseIcon,
  Edit as EditIcon,
  Visibility as LogsIcon
} from '@mui/icons-material';
import googleSheetsService from '../../services/googleSheetsService';
import CronBuilder from './CronBuilder';

export default function GoogleSheetsSchedulesSection({ onNotification }) {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSheet, setEditingSheet] = useState(null);
  const [editingCron, setEditingCron] = useState('');
  const [cronError, setCronError] = useState(null);

  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedJobLogs, setSelectedJobLogs] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsSheetName, setLogsSheetName] = useState('');

  const loadSheets = async () => {
    try {
      setLoading(true);
      const res = await googleSheetsService.getImports();
      const scheduled = (res.imports || []).filter(s => s.cron_expression);
      setSheets(scheduled);
      setError(null);
    } catch (err) {
      console.error('Error loading Google Sheets schedules:', err);
      setError('Failed to load Google Sheets schedules');
      if (onNotification) {
        onNotification('Failed to load Google Sheets schedules', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSheets();

    const interval = setInterval(loadSheets, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleToggleSchedule = async (sheet) => {
    try {
      setBusyId(sheet.id);
      const nextEnabled = !sheet.schedule_enabled;
      await googleSheetsService.updateSchedule(sheet.id, sheet.cron_expression, nextEnabled);
      if (onNotification) {
        onNotification(
          `Schedule ${nextEnabled ? 'resumed' : 'paused'} for ${sheet.sheet_name}`,
          'success'
        );
      }
      await loadSheets();
    } catch (err) {
      console.error('Error toggling schedule:', err);
      if (onNotification) {
        onNotification('Failed to update schedule', 'error');
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleRunNow = async (sheet) => {
    try {
      setBusyId(sheet.id);
      await googleSheetsService.refresh(sheet.id);
      if (onNotification) {
        onNotification(`Refresh started for ${sheet.sheet_name}`, 'success');
      }
      await loadSheets();
    } catch (err) {
      console.error('Error running sheet refresh:', err);
      if (onNotification) {
        onNotification('Failed to trigger refresh', 'error');
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleOpenEdit = (sheet) => {
    setEditingSheet(sheet);
    setEditingCron(sheet.cron_expression || '0 2 * * *');
    setCronError(null);
    setEditDialogOpen(true);
  };

  const handleViewLogs = async (sheet) => {
    setLogsSheetName(`${sheet.spreadsheet_name || sheet.spreadsheet_id} — ${sheet.sheet_name}`);
    setLoadingLogs(true);
    setLogsDialogOpen(true);
    try {
      const res = await googleSheetsService.getJobHistory(sheet.id, 20);
      setSelectedJobLogs(res.jobs || []);
    } catch (err) {
      console.error('Error loading Google Sheets job logs:', err);
      setSelectedJobLogs([]);
      if (onNotification) {
        onNotification('Failed to load job history', 'error');
      }
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingSheet) return;
    if (!editingCron || editingCron.trim().split(/\s+/).length !== 5) {
      setCronError('Invalid cron expression');
      return;
    }
    try {
      setBusyId(editingSheet.id);
      await googleSheetsService.updateSchedule(
        editingSheet.id,
        editingCron,
        editingSheet.schedule_enabled
      );
      if (onNotification) {
        onNotification(`Schedule updated for ${editingSheet.sheet_name}`, 'success');
      }
      setEditDialogOpen(false);
      setEditingSheet(null);
      await loadSheets();
    } catch (err) {
      console.error('Error updating schedule:', err);
      if (onNotification) {
        onNotification('Failed to update schedule', 'error');
      }
    } finally {
      setBusyId(null);
    }
  };

  const formatCron = (cron) => {
    if (!cron) return '—';
    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 5) return cron;
    const [min, hr, , , dow] = parts;
    let freq = 'Daily';
    if (hr.startsWith('*/')) freq = `Every ${hr.substring(2)}h`;
    else if (hr === '*') freq = 'Hourly';
    else if (dow === '1-5') freq = 'Weekdays';
    else if (dow === '0,6' || dow === '6,0') freq = 'Weekends';
    else if (dow !== '*') {
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      freq = dow.split(',').map(d => dayNames[parseInt(d)] || d).join(', ');
    }
    if (hr !== '*' && !hr.startsWith('*/')) {
      const h = parseInt(hr);
      const m = parseInt(min);
      const period = h >= 12 ? 'PM' : 'AM';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return `${freq} at ${h12}:${m.toString().padStart(2, '0')} ${period}`;
    }
    return freq;
  };

  const formatNextRun = (timestamp) => {
    if (!timestamp || timestamp === '1970-01-01 00:00:00') return 'Not scheduled';
    // ClickHouse returns DateTime as 'YYYY-MM-DD HH:MM:SS' (UTC, no TZ marker).
    // Without the 'Z', JS parses it as local time and shifts by the user's UTC offset.
    const time = new Date(typeof timestamp === 'string' && timestamp.includes(' ') ? timestamp.replace(' ', 'T') + 'Z' : timestamp);
    const now = new Date();
    if (time <= now) return 'Due now';
    const diffMs = time - now;
    const diffMins = Math.ceil(diffMs / (1000 * 60));
    if (diffMins < 60) return `In ${diffMins}m`;
    const diffHours = Math.ceil(diffMins / 60);
    if (diffHours < 24) return `In ${diffHours}h`;
    return time.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp || timestamp === '1970-01-01 00:00:00') return 'Never';
    const now = new Date();
    // ClickHouse returns DateTime as 'YYYY-MM-DD HH:MM:SS' (UTC, no TZ marker).
    // Without the 'Z', JS parses it as local time and shifts by the user's UTC offset.
    const time = new Date(typeof timestamp === 'string' && timestamp.includes(' ') ? timestamp.replace(' ', 'T') + 'Z' : timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <Paper sx={{ p: 4, mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading Google Sheets schedules...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        {error}
      </Alert>
    );
  }

  if (sheets.length === 0) return null;

  return (
    <Paper sx={{ mt: 4 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" fontWeight="600">
            Google Sheets Schedules
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Per-sheet scheduled imports from Google Sheets
          </Typography>
        </Box>
        <Tooltip title="Manage in Google Sheets page">
          <IconButton size="small" href="/google-sheets">
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sheet</TableCell>
              <TableCell>Table</TableCell>
              <TableCell>Mode</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Last Run</TableCell>
              <TableCell>Next Run</TableCell>
              <TableCell align="right">Rows</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sheets.map((s) => (
              <TableRow key={s.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {s.spreadsheet_name || s.spreadsheet_id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tab: {s.sheet_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {s.clickhouse_table}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={s.refresh_mode === 'additive' ? 'Additive' : 'Full'}
                    size="small"
                    color={s.refresh_mode === 'additive' ? 'info' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="500" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ScheduleIcon fontSize="small" color="action" />
                      {formatCron(s.cron_expression)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace', fontSize: '10px' }}>
                      {s.cron_expression}
                    </Typography>
                    <Chip
                      label={s.schedule_enabled ? 'Active' : 'Paused'}
                      color={s.schedule_enabled ? 'success' : 'default'}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatRelativeTime(s.last_fetched_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {formatNextRun(s.next_scheduled)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {s.row_count?.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title={s.schedule_enabled ? 'Pause Schedule' : 'Resume Schedule'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleSchedule(s)}
                          disabled={busyId === s.id}
                        >
                          {s.schedule_enabled ? <PauseIcon fontSize="small" /> : <RunIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Run Now">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleRunNow(s)}
                          disabled={busyId === s.id}
                          color="primary"
                        >
                          <RunIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="View Job History">
                      <IconButton
                        size="small"
                        onClick={() => handleViewLogs(s)}
                        color="info"
                      >
                        <LogsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Edit Schedule">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEdit(s)}
                          disabled={busyId === s.id}
                          color="secondary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Schedule</DialogTitle>
        <DialogContent>
          {editingSheet && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Sheet:</strong> {editingSheet.spreadsheet_name} — {editingSheet.sheet_name}
              </Typography>
              <CronBuilder
                value={editingCron}
                onChange={setEditingCron}
                error={cronError}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            color="primary"
            disabled={busyId === editingSheet?.id}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={logsDialogOpen} onClose={() => setLogsDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Google Sheets Job History{logsSheetName ? ` — ${logsSheetName}` : ''}</DialogTitle>
        <DialogContent>
          {loadingLogs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedJobLogs && selectedJobLogs.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Job ID</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Triggered By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedJobLogs.map((job) => {
                    let metadata = {};
                    try {
                      metadata = JSON.parse(job.job_metadata || '{}');
                    } catch (e) {
                      // Ignore parse errors
                    }
                    const triggeredBy = metadata.triggeredBy || job.sync_type || 'unknown';

                    return (
                      <TableRow key={job.id}>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">
                            {job.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {job.started_at ? new Date(job.started_at.replace(' ', 'T') + 'Z').toLocaleString() : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {job.completed_at ? new Date(job.completed_at.replace(' ', 'T') + 'Z').toLocaleString() : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={job.status}
                            size="small"
                            color={
                              job.status === 'completed' ? 'success' :
                              job.status === 'failed' ? 'error' :
                              job.status === 'running' ? 'primary' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {job.duration_minutes != null ? `${job.duration_minutes}m` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {job.progress_percentage}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={triggeredBy}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              No job history found for this sheet.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
